// Terrain/trees/water construction from map payload. Render-side only — floats
// welcome here; cosmetic randomness must be hash-seeded per tile (stable visuals).

import * as THREE from 'three';
import { Terrain } from '../shared/constants';
import { hash2 } from '../shared/rng';
import type { WorkerToMain } from '../shared/protocol';

type MapMsg = Extract<WorkerToMain, { type: 'map' }>;

export const ELEV_Y_SCALE = 6 / 255; // u8 elevation -> world Y
const CHUNK = 16;

const TERRAIN_COLORS: Record<number, THREE.Color> = {
  [Terrain.Water]: new THREE.Color('#2a63a8'), // (underwater ground; water plane sits above)
  [Terrain.Sand]: new THREE.Color('#d9c58a'),
  [Terrain.Grass]: new THREE.Color('#69a244'),
  [Terrain.Forest]: new THREE.Color('#4d8a36'),
  [Terrain.Rock]: new THREE.Color('#8a8578'),
};

export interface TerrainBuild {
  group: THREE.Group; // terrain chunk meshes (raycast targets)
  waterMesh: THREE.Mesh;
  trees: THREE.InstancedMesh;
  /** corner-averaged ground height lookup in world units */
  heightAt(x: number, y: number): number;
  /** rebuild the tree instances after harvest events (throttled by caller) */
  rebuildTrees(treeDensity: Uint8Array): void;
}

export function buildTerrain(map: MapMsg): TerrainBuild {
  const { width, height } = map.info;
  const { terrain, elevation, treeDensity } = map;

  const elevAt = (x: number, y: number): number => {
    const cx = Math.min(width - 1, Math.max(0, x));
    const cy = Math.min(height - 1, Math.max(0, y));
    return elevation[cy * width + cx];
  };
  // corner height = average of the 4 tiles meeting at the corner
  const cornerY = (x: number, y: number): number =>
    ((elevAt(x - 1, y - 1) + elevAt(x, y - 1) + elevAt(x - 1, y) + elevAt(x, y)) / 4) *
    ELEV_Y_SCALE;

  const group = new THREE.Group();
  group.name = 'terrain';
  const material = new THREE.MeshLambertMaterial({ vertexColors: true });

  const chunksX = Math.ceil(width / CHUNK);
  const chunksY = Math.ceil(height / CHUNK);
  for (let cy = 0; cy < chunksY; cy++) {
    for (let cx = 0; cx < chunksX; cx++) {
      const x0 = cx * CHUNK;
      const y0 = cy * CHUNK;
      const w = Math.min(CHUNK, width - x0);
      const h = Math.min(CHUNK, height - y0);
      const positions = new Float32Array(w * h * 6 * 3);
      const colors = new Float32Array(w * h * 6 * 3);
      let p = 0;
      for (let ty = y0; ty < y0 + h; ty++) {
        for (let tx = x0; tx < x0 + w; tx++) {
          const t = terrain[ty * width + tx];
          const c = TERRAIN_COLORS[t] ?? TERRAIN_COLORS[Terrain.Grass];
          const y00 = cornerY(tx, ty);
          const y10 = cornerY(tx + 1, ty);
          const y01 = cornerY(tx, ty + 1);
          const y11 = cornerY(tx + 1, ty + 1);
          // two triangles, flat-colored by tile
          const verts = [
            [tx, y00, ty],
            [tx, y01, ty + 1],
            [tx + 1, y10, ty],
            [tx + 1, y10, ty],
            [tx, y01, ty + 1],
            [tx + 1, y11, ty + 1],
          ];
          for (const [vx, vy, vz] of verts) {
            positions[p] = vx;
            positions[p + 1] = vy;
            positions[p + 2] = vz;
            colors[p] = c.r;
            colors[p + 1] = c.g;
            colors[p + 2] = c.b;
            p += 3;
          }
        }
      }
      const geo = new THREE.BufferGeometry();
      geo.setAttribute('position', new THREE.BufferAttribute(positions, 3));
      geo.setAttribute('color', new THREE.BufferAttribute(colors, 3));
      geo.computeVertexNormals();
      const mesh = new THREE.Mesh(geo, material);
      mesh.name = 'terrain-chunk';
      group.add(mesh);
    }
  }

  // water plane just above the water band
  const waterY = 79 * ELEV_Y_SCALE;
  const waterGeo = new THREE.PlaneGeometry(width, height);
  const waterMat = new THREE.MeshLambertMaterial({
    color: '#3f7fca',
    transparent: true,
    opacity: 0.75,
  });
  const waterMesh = new THREE.Mesh(waterGeo, waterMat);
  waterMesh.rotation.x = -Math.PI / 2;
  waterMesh.position.set(width / 2, waterY, height / 2);
  waterMesh.name = 'water';

  // trees: instanced cones, deterministic per-tile jitter via hash2
  const treeGeo = new THREE.ConeGeometry(0.32, 1.1, 6);
  const treeMat = new THREE.MeshLambertMaterial({ color: '#2f6b2a' });

  const buildTrees = (density: Uint8Array): THREE.InstancedMesh => {
    const treeTiles: Array<{ x: number; y: number; n: number }> = [];
    let total = 0;
    for (let ty = 0; ty < height; ty++) {
      for (let tx = 0; tx < width; tx++) {
        const d = density[ty * width + tx];
        if (d > 0) {
          const n = 1 + (d >> 7); // 1..2 trees per tile
          treeTiles.push({ x: tx, y: ty, n });
          total += n;
        }
      }
    }
    const mesh = new THREE.InstancedMesh(treeGeo, treeMat, Math.max(1, total));
    const m = new THREE.Matrix4();
    let inst = 0;
    for (const { x, y, n } of treeTiles) {
      for (let k = 0; k < n; k++) {
        const hx = hash2(x, y, 0x7ee5 + k);
        const jx = ((hx & 0xff) / 255) * 0.7 + 0.15;
        const jy = (((hx >>> 8) & 0xff) / 255) * 0.7 + 0.15;
        const scale = 0.8 + (((hx >>> 16) & 0xff) / 255) * 0.5;
        const wx = x + jx;
        const wz = y + jy;
        const wy = cornerY(Math.round(wx), Math.round(wz)) + (1.1 * scale) / 2;
        m.makeScale(scale, scale, scale);
        m.setPosition(wx, wy, wz);
        mesh.setMatrixAt(inst++, m);
      }
    }
    mesh.count = inst;
    mesh.instanceMatrix.needsUpdate = true;
    mesh.name = 'trees';
    return mesh;
  };

  const result: TerrainBuild = {
    group,
    waterMesh,
    trees: buildTrees(treeDensity),
    heightAt: cornerY,
    rebuildTrees(density: Uint8Array): void {
      result.trees.dispose();
      result.trees = buildTrees(density);
    },
  };
  return result;
}
